import {
  CalendarEntry,
  CircularLetter,
  getCalendarEntries,
  getCircularLetters,
  getMessages,
  ReadMessage,
} from "@/api/openapi";
import { PspContext } from "@/config/contexts";
import { translate } from "@/config/translator";
import { useContext, useState, useEffect } from "react";
import { TileArgs } from "react-calendar";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { tileContentFunc } from "../homeUtils";
import { TableColumn } from "@/config/types";
import { isSameDay } from "date-fns";

export function useHomeDesktop() {
  // -----------------------------------------------------------------------------
  // GENERAL
  // -----------------------------------------------------------------------------
  const { lang, selectedMandate, selectedMandateId } = useContext(PspContext);
  const navigate = useNavigate();

  // -----------------------------------------------------------------------------
  // STATES
  // -----------------------------------------------------------------------------
  const [selectedCalendarEntries, setSelectedCalendarEntries] = useState<
    CalendarEntry[] | null
  >(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [calendarEntries, setCalendarEntries] = useState<CalendarEntry[]>();
  const [circularLetters, setCircularLetters] = useState<CircularLetter[]>();
  const [messages, setMessages] = useState<ReadMessage[]>();
  const [isLoadingCalendarEntries, setIsLoadingCalendarEntries] =
    useState(true);
  const [isLoadingCircularLetters, setIsLoadingCircularLetters] =
    useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);

  // -----------------------------------------------------------------------------
  // FETCH INITIAL DATA
  // -----------------------------------------------------------------------------
  useEffect(() => {
    if (selectedMandateId) {
      getCalendarEntries({ query: { mandate_id: selectedMandateId } }).then(
        ({ data, error }) => {
          if (error) {
            toast(translate("couldntLoadCalendarEntries", lang));
            throw error;
          }
          setCalendarEntries(data);
          setIsLoadingCalendarEntries(false);
          const todayCalendarEntries = data.filter((entry) =>
            selectedDate
              ? isSameDay(new Date(entry.date), selectedDate)
              : false,
          );
          if (todayCalendarEntries.length !== 0) {
            setSelectedCalendarEntries(todayCalendarEntries);
          }
        },
      );
    } else {
      toast(translate("noMandateSelected", lang));
    }
    getCircularLetters({ query: { limit: 50 } }).then(({ data, error }) => {
      if (error) {
        toast(translate("couldntLoadCircularLetters", lang));
      } else {
        setCircularLetters(data);
        setIsLoadingCircularLetters(false);
      }
    });
    getMessages({ query: { limit: 20, offset: 0 } }).then(({ data, error }) => {
      if (error) {
        toast(translate("couldntLoadMessages", lang));
      } else {
        setMessages(data);
        setIsLoadingMessages(false);
      }
    });
  }, [lang, selectedDate, selectedMandateId]);

  // -----------------------------------------------------------------------------
  // MEMOIZED VALUES
  // -----------------------------------------------------------------------------
  const circularLettersColumns: TableColumn[] = [
    { key: "type", label: translate("type", lang), className: "w-10" },
    { key: "title", label: translate("title", lang), className: "w-3/4" },
    {
      key: "date",
      label: translate("date", lang),
      className: "min-w-32 w-1/4",
    },
    {
      key: "action",
      label: translate("action", lang),
      className: "w-30",
    },
  ];

  const messagesColumns: TableColumn[] = [
    { key: "name", label: translate("title", lang), className: "w-1/2" },
    {
      key: "date",
      label: translate("date", lang),
      className: "min-w-24 w-1/4",
    },
    {
      key: "status",
      label: translate("status", lang),
      className: "min-w-32 w-1/4",
    },
    {
      key: "action",
      label: translate("action", lang),
      className: "w-16",
    },
  ];

  const isLoading =
    isLoadingCircularLetters || isLoadingCalendarEntries || isLoadingMessages;

  // -----------------------------------------------------------------------------
  // CALLBACK FUNCTIONS
  // -----------------------------------------------------------------------------
  function tileContentFuncWrapper({ date }: TileArgs) {
    return tileContentFunc(
      date,
      selectedDate,
      setSelectedDate,
      setSelectedCalendarEntries,
      calendarEntries ?? [],
    );
  }

  return {
    lang,
    isLoading,
    tileContentFuncWrapper,
    selectedCalendarEntries,
    selectedDate,
    circularLettersColumns,
    circularLetters,
    messagesColumns,
    messages,
    selectedMandate,
    navigate,
  };
}
